/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchContext, RecentSearches } from './SearchContextProvider'
import Tabs from '../../components/SearchResultsPanel/Tabs'
import SearchSection from './SearchSection'
import SearchTopResult from './SearchTopResult'
import { OrgRow, PlayerRow, SchedRow, TeamRow, VenueRow } from './SearchRow'
import SearchContainer from './SearchContainer'
import {
  clearRecentSearches,
  getRecentSearches,
  storeRecentSearchItem,
} from './recent-search'
import SearchRecentPanel from './SearchRecentPanel'
import Text from 'src/components/ts-components/Text'
import { Spin } from 'antd'
import {
  createEmptyResults,
  SearchResponseItem,
  useSearchResults,
} from './useSearchResults'
import { useModalScroll } from 'src/hooks/useModalScroll'

const SectionSeparator = () => (
  <div
    css={css`
      height: 1px;
      background-color: #3a4659;
      // margin: 8px 0;
    `}
  />
)

interface SearchProps {
  userId?: string | number
}
const Search: React.FC<SearchProps> = ({ userId }) => {
  const pathname =
    typeof window !== 'undefined' ? window.location?.pathname : ''
  const searchContext = useSearchContext()
  const {
    dispatch,
    isActive,
    setIsActive,
    searchText,
    filter,
    isTeamOrSchedulePage,
    displayContents,
    loadingResults,
    loadingMore,
    results,
    numResults,
    page,
    pagination,
    error,
    topResult,
    totalResults,
    isSeeAllActive,
    recentSearches,
    setFilter: updateFilter,
    setText,
    searchInputRef,
  } = searchContext

  const containerRef = useRef<HTMLDivElement>(null)
  const resultsContainerRef = useRef<HTMLDivElement>(null)
  const { openModal, closeModal } = useModalScroll()

  const normalizedResults = useMemo(
    () => results ?? createEmptyResults(),
    [results]
  )

  const {
    players: playerResults,
    teams: teamResults,
    scheds: schedResults,
    orgs: orgResults,
    venues: venueResults,
  } = normalizedResults

  const playerCount = playerResults.length
  const teamCount = teamResults.length
  const schedCount = schedResults.length
  const orgCount = orgResults.length
  const venuesCount = venueResults.length

  const tabs = useMemo(
    () => [
      { label: 'All', value: '', count: numResults || undefined },
      {
        label: 'Organizations',
        value: 'organization',
        count: totalResults.organization,
      },
      {
        label: 'Competitions',
        value: 'schedule',
        count: totalResults.schedule,
      },
      {
        label: 'Teams',
        value: 'team',
        count: totalResults.team,
      },
      {
        label: 'Players',
        value: 'player',
        count: totalResults.player,
      },
      {
        label: isCanlan ? 'Sports Complexes' : 'Locations',
        value: 'venue',
        count: totalResults.venue,
      },
    ],
    [numResults, totalResults]
  )

  const updateRecentSearches = useCallback((value: RecentSearches) => {
    dispatch({ type: 'SET_RECENT_SEARCHES', payload: value })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onClose = useCallback(() => {
    setIsActive(false)
    dispatch({ type: 'SET_DISPLAY_CONTENTS', payload: false })
    setText('')
  }, [])

  const onResultClick = useCallback(
    (item: SearchResponseItem) => {
      onClose()
      const store = storeRecentSearchItem(item)
      if (store) {
        updateRecentSearches(store as RecentSearches)
      }
    },
    [onClose, updateRecentSearches]
  )

  const handleSeeAllButton = useCallback(
    (type: string, shouldSetFilter: boolean) => {
      updateFilter(shouldSetFilter ? type : '')
    },
    []
  )

  const handleTabClick = useCallback((value: string) => {
    updateFilter(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const seeAllHandlers = useMemo(
    () => ({
      player: (shouldSetFilter: boolean) =>
        handleSeeAllButton('player', shouldSetFilter),
      schedule: (shouldSetFilter: boolean) =>
        handleSeeAllButton('schedule', shouldSetFilter),
      organization: (shouldSetFilter: boolean) =>
        handleSeeAllButton('organization', shouldSetFilter),
      team: (shouldSetFilter: boolean) =>
        handleSeeAllButton('team', shouldSetFilter),
      venue: (shouldSetFilter: boolean) =>
        handleSeeAllButton('venue', shouldSetFilter),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const {
    player: playerSeeAllHandler,
    schedule: scheduleSeeAllHandler,
    organization: organizationSeeAllHandler,
    team: teamSeeAllHandler,
    venue: venueSeeAllHandler,
  } = seeAllHandlers

  useSearchResults({
    searchText,
    page,
    filter,
    dispatch,
    updateRecentSearches,
  })

  // Handle Escape key press to close search
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isActive) {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive])

  // Handle click outside to close search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isActive &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isActive, onClose, searchInputRef])

  useEffect(() => {
    if (displayContents) {
      openModal()
    } else {
      closeModal()
    }
    return () => {
      closeModal()
    }
  }, [displayContents, openModal, closeModal])

  useEffect(() => {
    if (userId) {
      const store = getRecentSearches()
      if (
        store &&
        !Array.isArray(store) &&
        (store as RecentSearches)?.uid === userId
      ) {
        updateRecentSearches(store as RecentSearches)
      } else {
        clearRecentSearches(userId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  useEffect(() => {
    const newValue = /team\/|schedule\//.test(pathname ?? '')
    dispatch({ type: 'SET_IS_TEAM_OR_SCHEDULE_PAGE', payload: newValue })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  useEffect(() => {
    const shouldShow = isActive

    if (shouldShow) {
      dispatch({ type: 'SET_DISPLAY_CONTENTS', payload: true })

      if (searchText.length > 0) {
        dispatch({ type: 'SET_PAGE', payload: 1 })
      }
    } else {
      dispatch({ type: 'SET_DISPLAY_CONTENTS', payload: false })
    }
  }, [dispatch, isActive, searchText])

  useEffect(() => {
    dispatch({ type: 'SET_LOADING_RESULTS', payload: true })
    dispatch({ type: 'SET_PAGE', payload: 1 })
    dispatch({
      type: 'SET_IS_SEE_ALL_ACTIVE',
      payload: filter.length === 0,
    })
  }, [dispatch, filter])

  const handleScroll = useCallback(() => {
    if (!resultsContainerRef.current) {
      return
    }

    // Don't load more if on 'All' tab (filter is empty)
    if (!filter) {
      return
    }

    const { scrollTop, scrollHeight, clientHeight } =
      resultsContainerRef.current
    const SCROLL_THRESHOLD = 100
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD

    const hasMore =
      pagination.current_page &&
      pagination.last_page &&
      pagination.current_page < pagination.last_page

    if (nearBottom && hasMore && !loadingResults && !loadingMore) {
      dispatch({
        type: 'SET_PAGE',
        payload: (pagination.current_page || 0) + 1,
      })
    }
  }, [
    pagination.current_page,
    pagination.last_page,
    loadingResults,
    loadingMore,
    dispatch,
    filter,
  ])

  useEffect(() => {
    const container = resultsContainerRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  const players = useMemo(() => {
    if (playerCount === 0) {
      return null
    }
    return (
      <SearchSection
        key='players'
        results={playerResults}
        onResultClick={onResultClick}
        onSeeAll={playerSeeAllHandler}
        isSeeAllActive={isSeeAllActive}
        RowComponent={PlayerRow}
        titlei18nKey='player'
        totalResults={totalResults.player}
      />
    )
  }, [
    isSeeAllActive,
    onResultClick,
    playerCount,
    playerResults,
    playerSeeAllHandler,
    totalResults.player,
  ])

  const schedules = useMemo(() => {
    if (schedCount === 0) {
      return null
    }
    return (
      <SearchSection
        key='schedules'
        results={schedResults}
        onResultClick={onResultClick}
        onSeeAll={scheduleSeeAllHandler}
        isSeeAllActive={isSeeAllActive}
        RowComponent={SchedRow}
        titlei18nKey='competition'
        isFullWidth
        totalResults={totalResults.schedule}
      />
    )
  }, [
    isSeeAllActive,
    onResultClick,
    schedCount,
    schedResults,
    scheduleSeeAllHandler,
    totalResults.schedule,
  ])

  const orgs = useMemo(() => {
    if (orgCount === 0) {
      return null
    }
    return (
      <SearchSection
        key='orgs'
        results={orgResults}
        onResultClick={onResultClick}
        onSeeAll={organizationSeeAllHandler}
        isSeeAllActive={isSeeAllActive}
        RowComponent={OrgRow}
        titlei18nKey='organization'
        totalResults={totalResults.organization}
      />
    )
  }, [
    isSeeAllActive,
    onResultClick,
    orgCount,
    orgResults,
    organizationSeeAllHandler,
    totalResults.organization,
  ])

  const teams = useMemo(() => {
    if (teamCount === 0) {
      return null
    }
    return (
      <SearchSection
        key='teams'
        results={teamResults}
        onResultClick={onResultClick}
        onSeeAll={teamSeeAllHandler}
        isSeeAllActive={isSeeAllActive}
        RowComponent={TeamRow}
        titlei18nKey='team'
        totalResults={totalResults.team}
      />
    )
  }, [
    isSeeAllActive,
    onResultClick,
    teamCount,
    teamResults,
    teamSeeAllHandler,
    totalResults.team,
  ])

  const venues = useMemo(() => {
    if (venuesCount === 0) {
      return null
    }
    return (
      <SearchSection
        key='venues'
        results={venueResults}
        onResultClick={onResultClick}
        onSeeAll={venueSeeAllHandler}
        isSeeAllActive={isSeeAllActive}
        RowComponent={VenueRow}
        titlei18nKey='location'
        totalResults={totalResults.venue}
      />
    )
  }, [
    isSeeAllActive,
    onResultClick,
    totalResults.venue,
    venueResults,
    venueSeeAllHandler,
    venuesCount,
  ])

  const sectionsList = useMemo(
    () => [players, teams, orgs, venues],
    [orgs, players, teams, venues]
  )

  const { firstResult, remainingResults } = useMemo(() => {
    let first: React.ReactNode | null = null
    let remainder: Array<React.ReactNode | null> = []

    for (let i = 0; i < sectionsList.length; i += 1) {
      const section = sectionsList[i]
      if (section) {
        first = section
        remainder = sectionsList.slice(i + 1)
        break
      }
    }

    return { firstResult: first, remainingResults: remainder }
  }, [sectionsList])

  return (
    // @ts-ignore
    <SearchContainer
      ref={containerRef}
      displayContents={displayContents}
      isTeamOrSchedulePage={isTeamOrSchedulePage}
    >
      {searchText.length === 0 ? (
        <SearchRecentPanel
          recentSearches={recentSearches}
          setRecentSearches={updateRecentSearches}
          onClose={onClose}
        />
      ) : (
        <>
          {/* Tabs for filtering */}
          <div
            css={css`
              padding: 16px 16px 0 16px;
            `}
          >
            <Tabs activeTab={filter} tabs={tabs} onTabClick={handleTabClick} />
          </div>

          {/* Content area */}
          <div
            ref={resultsContainerRef}
            css={css`
              padding: 16px;
              max-height: 540px;
              overflow-y: auto;
              overflow-x: hidden;

              /* Custom scrollbar styling */
              &::-webkit-scrollbar {
                width: 16px;
              }

              &::-webkit-scrollbar-track {
                background: ${colors.SECONDARY_300};
                border-radius: 8px;
                margin: 4px 0;
              }

              &::-webkit-scrollbar-thumb {
                background: ${colors.SECONDARY_100};
                border-radius: 32px;
                border: 4px solid ${colors.SECONDARY_300};
              }

              &::-webkit-scrollbar-thumb:hover {
                background: ${colors.NEUTRAL_100};
              }

              scrollbar-width: thin;
              scrollbar-color: ${colors.SECONDARY_100} ${colors.SECONDARY_300};
            `}
          >
            {error ? (
              <div
                css={css`
                  color: ${colors.ERROR_300};
                  text-align: center;
                  padding: 40px 20px;
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                `}
              >
                {error}
              </div>
            ) : loadingResults ? (
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: 200px;
                `}
              >
                <Spin size='small' style={{ color: colors.PRIMARY }} />
              </div>
            ) : numResults === 0 ? (
              <div
                css={css`
                  text-align: center;
                  padding: 40px 20px;
                  color: ${colors.NEUTRAL_50};
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                `}
              >
                <p>No results found for &quot;{searchText}&quot;</p>
              </div>
            ) : (
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 16px;
                `}
              >
                {isSeeAllActive ? (
                  <>
                    {Object.keys(topResult).length > 0 && (
                      <div>
                        <Text
                          variant='paragraphXSmall'
                          weight='semiBold'
                          color={colors.NEUTRAL_50}
                          style={`
                            margin-bottom: 8px;
                            text-transform: uppercase;
                            letter-spacing: 1.6px;
                          `}
                        >
                          Top Result
                        </Text>{' '}
                        <SearchTopResult
                          item={topResult}
                          onClick={() => {
                            if (
                              topResult.type !== 'venue' &&
                              topResult.type !== 'team_official'
                            ) {
                              onResultClick(topResult)
                            }
                          }}
                        />
                      </div>
                    )}
                    {Object.keys(topResult).length > 0 &&
                      (firstResult || schedules) && <SectionSeparator />}
                    {firstResult}
                    {firstResult && schedules && <SectionSeparator />}
                    {schedules}
                    {(firstResult ||
                      schedules ||
                      Object.keys(topResult).length > 0) &&
                      remainingResults.some((r) => r) && <SectionSeparator />}
                    {remainingResults.map((result, idx) => (
                      <React.Fragment key={idx}>
                        {idx > 0 &&
                          result &&
                          remainingResults.slice(0, idx).some((r) => r) && (
                            <SectionSeparator />
                          )}
                        {result}
                      </React.Fragment>
                    ))}
                  </>
                ) : (
                  [...sectionsList, schedules]
                )}

                {loadingMore && (
                  <div
                    css={css`
                      display: flex;
                      justify-content: center;
                      padding: 16px;
                    `}
                  >
                    <Spin size='small' style={{ color: colors.PRIMARY }} />
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </SearchContainer>
  )
}

export default Search
